import { useEffect, useState } from 'react';
import * as S from './styles';
import { RootStateCarteira } from '../../store/carteiraReducer';
import { connectUtil, PropsFromRedux } from 'base/src/utils/reduxUtil';
import { PageBanner } from 'base/src/components/PageBanner';
import { Tabs, TabsContent } from 'base/src/components/Tabs';
import BoletoCard, { Boleto } from './components/BoletoCard';
import { BoletoResponse } from '../../types/boleto/boletoResponse';
import { GetBoletos, AbrirBoleto } from '../../store/deposito/actions/depositoAction';
import { DepositoBoletoResponse } from '../../types/deposito/depositoBoletoResponse';

const connector = connectUtil(
  (state: RootStateCarteira) => ({
    usuarioState: state.CarteiraBaseReducer.CarteiraUsuario,
    boletos: state.DepositoReducer.GetBoletos || [],
  }),
  { GetBoletos, AbrirBoleto }
);

const statusTabMapping = {
  'todos': [1, 2, 3],
  'pagos': [1],
  'erro': [3],
  'em_espera': [2],
};

export interface BoletoGestaoProps extends PropsFromRedux<typeof connector> {
}

function BoletoGestao(props: BoletoGestaoProps) {
  const [activeTab, setActiveTab] = useState('todos');
  const { boletos = [] } = props;

  useEffect(() => {
    handleGetBoletos();
  }, []);

  function handleGetBoletos() {
    props.GetBoletos(props.usuarioState[0].CarteiraID);
  }

  function handleBoletoClick(boleto: Boleto) {
    const boletoResponse: DepositoBoletoResponse = {
      PaymentUrl: boleto.blobUrl,
      Success: true,
      GeneratedId: Number(boleto.id) || 0,
      Msg: '',
      TransactionId: '',
    };

    props.AbrirBoleto(boletoResponse);
  }

  function mapResponseToBoleto(boletoResponse: BoletoResponse): Boleto {
    let uiStatus: 'Em aberto' | 'pago' | 'vencido' | 'cancelado' | 'duplicado' = 'Em aberto';

    const status = typeof boletoResponse.Status === 'number' ? boletoResponse.Status : 0;
    const duplicate = isDuplicate(boletoResponse);

    if (duplicate) {
      uiStatus = 'duplicado';
    } else {
      switch (status) {
        case 1:
          uiStatus = 'pago';
          break;
        case 2:
          uiStatus = 'Em aberto';
          break;
        case 3:
          uiStatus = 'vencido';
          break;
        default:
          uiStatus = 'Em aberto';
      }
    }

    return {
      id: boletoResponse.CarteiraID?.toString() || '',
      description: `Fatura #${boletoResponse.CarteiraID || ''}`,
      dataCriacao: boletoResponse.DataCriacao || new Date().toISOString(),
      blobUrl: boletoResponse.BlobUrl || '',
      blobToken: boletoResponse.BlobToken || '',
      amount: boletoResponse.Valor || '0',
      status: uiStatus,
      usuario: boletoResponse.Usuario || 'Usuário não informado'
    };
  }

  function getFilteredBoletos() {
    if (!boletos || boletos.length === 0) {
      return [];
    }

    let filteredBoletos = boletos;

    if (activeTab !== 'todos') {
      filteredBoletos = boletos.filter(boleto => {
        const status = typeof boleto.Status === 'number' ? boleto.Status : 0;
        return statusTabMapping[activeTab as keyof typeof statusTabMapping].includes(status);
      });
    }

    filteredBoletos = [...filteredBoletos].sort((a, b) => {
      const dateA = new Date(a.DataCriacao || 0).getTime();
      const dateB = new Date(b.DataCriacao || 0).getTime();
      return dateB - dateA; // Descending order (newest first)
    });

    return filteredBoletos.map(mapResponseToBoleto);
  }

  function countBoletos(tabKey: string): number {
    if (!boletos || boletos.length === 0) {
      return 0;
    }

    if (tabKey === 'todos') {
      return boletos.length;
    }

    return boletos.filter(boleto => {
      const status = typeof boleto.Status === 'number' ? boleto.Status : 0;
      return statusTabMapping[tabKey as keyof typeof statusTabMapping].includes(status);
    }).length;
  }

  const tabItems = [
    { value: 'todos', label: 'Todos', count: countBoletos('todos') },
    { value: 'pagos', label: 'Pagos', count: countBoletos('pagos') },
    { value: 'erro', label: 'Erro', count: countBoletos('erro') },
    { value: 'em_espera', label: 'Em Espera', count: countBoletos('em_espera') },
  ];

   function isDuplicate (boleto : BoletoResponse) : boolean{
      if (!boletos || boletos.length === 0) return false;
      
      const currentDate = new Date(boleto.DataCriacao);
      const currentDay = currentDate.getDate();
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();
      const currentAmount = boleto.Valor;
      
      return boletos.some(otherBoleto => {
        const otherDate = new Date(otherBoleto.DataCriacao);
        return otherBoleto.BlobToken !== boleto.BlobToken && 
          otherDate.getDate() === currentDay &&
          otherDate.getMonth() === currentMonth &&
          otherDate.getFullYear() === currentYear &&
          otherBoleto.Valor === currentAmount;
      });
    }

  return (
    <S.Container>
      <PageBanner title='Boletos' subtile='Visualize e gerencie seus boletos bancários' />

      <Tabs
        items={tabItems}
        defaultValue="todos"
        onValueChange={setActiveTab}
      >
        <TabsContent value="todos">
          {getFilteredBoletos().length > 0 ? (
            getFilteredBoletos().map(boleto => (
              <BoletoCard
                key={boleto.id}
                boleto={boleto}
                onClick={handleBoletoClick.bind(null, boleto)}
              />
            ))
          ) : (
            <div>Nenhum boleto encontrado.</div>
          )}
        </TabsContent>

        <TabsContent value="pagos">
          {getFilteredBoletos().length > 0 ? (
            getFilteredBoletos().map(boleto => (
              <BoletoCard
                key={boleto.id}
                boleto={boleto}
                onClick={handleBoletoClick.bind(null, boleto)}
              />
            ))
          ) : (
            <div>Nenhum boleto pago encontrado.</div>
          )}
        </TabsContent>

        <TabsContent value="erro">
          {getFilteredBoletos().length > 0 ? (
            getFilteredBoletos().map(boleto => (
              <BoletoCard
                key={boleto.id}
                boleto={boleto}
                onClick={handleBoletoClick.bind(null, boleto)}
              />
            ))
          ) : (
            <div>Nenhum boleto com erro encontrado.</div>
          )}
        </TabsContent>

        <TabsContent value="em_espera">
          {getFilteredBoletos().length > 0 ? (
            getFilteredBoletos().map(boleto => (
              <BoletoCard
                key={boleto.id}
                boleto={boleto}
                onClick={handleBoletoClick.bind(null, boleto)}
              />
            ))
          ) : (
            <div>Nenhum boleto em espera encontrado.</div>
          )}
        </TabsContent>
      </Tabs>
    </S.Container>
  );
}

export default connector(BoletoGestao);

